import type {AuditEvent} from '../types';
import type {Identity} from '../utils';
import {describeAuditEvent, formatDate, identityPrimary, statusLabel} from '../utils';
import {CopyCode} from './CopyCode';
import {Icon} from './Icon';

/** Affiche un acteur ou une cible sous sa forme la plus lisible connue, sans jamais masquer l'UID. */
function Party({uid, identities}: {uid: string; identities: Map<string, Identity>}) {
  const identity = identities.get(uid);
  const name = identity ? identityPrimary(identity) : '';
  return <>{name && `${name} `}<code>{uid.length > 12 ? `${uid.slice(0, 8)}…` : uid}</code></>;
}

export function AuditEventCard({event, identities, open = false}: {event: AuditEvent; identities: Map<string, Identity>; open?: boolean}) {
  const summary = describeAuditEvent(event);
  return <article className={open ? 'event open' : 'event'}>
    <div className={`ev-icon k-${summary.group}`}><Icon name={summary.icon} /></div>
    <div className="ev-main">
      <div className="ev-head">
        <h3>{summary.label}</h3>
        <span className="chip kind">{event.kind}</span>
        <time dateTime={new Date(event.appliedAt).toISOString()}>{formatDate(event.appliedAt)}</time>
      </div>
      {event.reason && <p className="reason">{event.reason}</p>}
      <dl className="ev-meta">
        <div><dt>Acteur</dt><dd><Party uid={event.actorUid} identities={identities} /></dd></div>
        {!!summary.targets.length && <div><dt>{summary.targets.length > 1 ? 'Cibles' : 'Cible'}</dt><dd>{summary.targets.map(target => <span key={target} className="x-target"><Party uid={target} identities={identities} /></span>)}</dd></div>}
        {summary.statusAfter && <div><dt>Statut après</dt><dd><span className={`chip st-${summary.statusAfter}`}>{statusLabel(summary.statusAfter)}</span></dd></div>}
      </dl>
      {!!summary.changes.length && <ul className="changes">{summary.changes.map(change => <li key={change}>{change}</li>)}</ul>}
      <details className="raw">
        <summary>Avant / après et identifiant</summary>
        <div className="raw-grid">
          <div><h4>Avant</h4><p className="muted">{summary.beforeText}</p></div>
          <div><h4>Après</h4><p>{summary.afterText}</p></div>
        </div>
        <p className="uid"><span className="muted">Événement</span> <code>{event.id}</code><CopyCode value={event.id} label="l’identifiant de l’événement" /></p>
      </details>
    </div>
  </article>;
}
